import type { ClientOfferReviewDecision, ClientOfferReviewUnit } from './client-offer-review-types';

export type ClientOfferReviewUnitStatus = 'sold' | 'accepted' | 'rejected' | 'pending';

export function unitReviewStatus(
  unit: ClientOfferReviewUnit,
  decision: ClientOfferReviewDecision,
): ClientOfferReviewUnitStatus {
  if (unit.sold) return 'sold';
  if (decision === 'accepted') return 'accepted';
  if (decision === 'rejected') return 'rejected';
  return 'pending';
}

/** 4px left stripe DS class per state. */
export const UNIT_CARD_STRIPE_CLASS: Record<ClientOfferReviewUnitStatus, string> = {
  sold: 'bg-status-unavailable',
  accepted: 'bg-status-free',
  rejected: 'bg-status-sold',
  pending: 'bg-border',
};

/**
 * Card border DS class — reuses the same `--status-*` tokens as
 * `UNIT_CARD_STRIPE_CLASS` above (unavailable for sold, free for accepted) instead
 * of a separate hand-rolled red/emerald palette. Active-but-undecided is a 4th,
 * transient state.
 */
export function unitCardBorderClassName(
  unit: ClientOfferReviewUnit,
  decision: ClientOfferReviewDecision,
  activeId: string | null,
): string {
  if (unit.sold) return 'border-status-unavailable';
  // Active must win over accepted — otherwise selecting an already-accepted unit (via
  // its locate button or the corresponding map pin) shows no visible highlight at all,
  // since the accepted branch previously short-circuited before this check ran.
  if (activeId === unit.id) return 'border-primary/50';
  if (decision === 'accepted') return 'border-status-free/40';
  return 'border-border';
}

/** Reuses the same `--status-free`/`--status-sold` tokens as the stripe/border above. */
export const UNIT_CARD_BADGE_CLASS: Record<'accepted' | 'rejected' | 'sold', string> = {
  accepted: 'bg-status-free/15 text-status-free',
  rejected: 'bg-status-sold/15 text-status-sold',
  sold: 'bg-muted text-muted-foreground',
};

/** Price cell value, DS text class, and strike state. */
export function unitPriceModel(
  unit: ClientOfferReviewUnit,
  isRejected: boolean,
  fmtPln: (v: number | null) => string | null,
  confirmationPendingLabel: string,
  netSuffixLabel: string,
) {
  const text =
    unit.sold && unit.price == null ? '—' : (fmtPln(unit.price) ?? confirmationPendingLabel);
  const className =
    unit.sold || isRejected
      ? 'text-muted-foreground/70'
      : unit.price
        ? 'text-foreground'
        : 'text-muted-foreground';
  const strike = (unit.sold || isRejected) && unit.price != null;
  return { text, className, strike, sub: unit.price ? netSuffixLabel : '' };
}

/**
 * Accept/reject button DS classes for a given decision + current button role.
 * Reuses `--status-free`/`--status-sold` for consistency with the stripe/border/badge
 * above; the active-reject state already used the (equally valid) `--destructive`
 * token and is left as-is.
 */
export function unitDecisionButtonClassName(active: boolean, kind: 'accept' | 'reject'): string {
  if (kind === 'accept') {
    return active
      ? 'border-status-free bg-status-free text-white'
      : 'border-status-free/40 text-status-free';
  }
  return active
    ? 'border-destructive bg-destructive text-white'
    : 'border-status-sold/40 text-status-sold';
}
